/**
 * AIGenerationManager Component
 *
 * Manages the AI blog post generation lifecycle including:
 * - Loading state with real-time phase updates
 * - Phase transitions (headline approval, outline approval, etc.)
 * - Error handling and success states
 *
 * Story: UX-003
 *
 * ARCHITECTURE NOTE: This component renders content directly (not via updateDrawer)
 * to avoid unmounting/remounting which cancels React Query polling.
 */
import { useState, useEffect } from 'react'
import { BlogGenerationLoadingState } from './BlogGenerationLoadingState'
import { HeadlineReviewContent } from './HeadlineReviewContent'
import { OutlineReviewContent } from './OutlineReviewContent'
import { LengthWarningContent } from './LengthWarningContent'
import { useBlogGenerationPolling } from '@/hooks/useBlogGenerationPolling'
import { useDrawer } from '@/components/ui/drawer'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'

interface AIGenerationManagerProps {
  postId: string
  drawerId: string
  onComplete?: (postId: string) => void
}

export function AIGenerationManager({ postId, drawerId, onComplete }: AIGenerationManagerProps) {
  const { closeDrawer } = useDrawer()
  const { currentPhase, post } = useBlogGenerationPolling({ postId })

  // Track when user has taken an action to prevent flickering back to approval screens
  const [actionTaken, setActionTaken] = useState<string | null>(null)

  // Clear actionTaken state when phase actually changes to prevent stale state
  useEffect(() => {
    if (!currentPhase) return

    // If we're in an active generation phase, clear any action state
    const activePhases = ['researching', 'generating_headlines', 'outlining', 'writing', 'optimizing']
    if (activePhases.includes(currentPhase)) {
      setActionTaken(null)
    }

    // If we reach a different approval phase, clear action state
    const approvalPhases = ['awaiting_headline_approval', 'awaiting_outline_approval', 'awaiting_length_decision']
    if (approvalPhases.includes(currentPhase)) {
      // Only clear if we're in a different approval phase than the action we took
      if (actionTaken === 'headline_approved' && currentPhase !== 'awaiting_headline_approval') {
        setActionTaken(null)
      }
      if (actionTaken === 'headline_revision' && currentPhase === 'awaiting_headline_approval') {
        // Headlines were regenerated, clear action so user can see new options
        setActionTaken(null)
      }
      if (actionTaken === 'outline_approved' && currentPhase !== 'awaiting_outline_approval') {
        setActionTaken(null)
      }
      if (actionTaken === 'outline_revision' && currentPhase === 'awaiting_outline_approval') {
        // Outline was regenerated, clear action so user can see new outline
        setActionTaken(null)
      }
    }
  }, [currentPhase, actionTaken])

  // Active generation phases - show loading state
  const activePhases = ['researching', 'generating_headlines', 'outlining', 'writing', 'optimizing']
  if (!currentPhase || activePhases.includes(currentPhase)) {
    return (
      <BlogGenerationLoadingState
        postId={postId}
        currentPhase={currentPhase}
        onCancel={() => closeDrawer(drawerId)}
      />
    )
  }

  // Terminal phase: Ready - Auto-redirect to editor
  if (currentPhase === 'ready') {
    // Show brief success message while redirecting
    if (onComplete) {
      // Call onComplete immediately to open editor
      setTimeout(() => onComplete(postId), 500)
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-card text-card-foreground">
        <div className="text-6xl mb-4">✅</div>
        <h3 className="text-2xl font-semibold text-foreground mb-2">Article complete!</h3>
        <p className="text-muted-foreground text-center">
          Opening editor...
        </p>
      </div>
    )
  }

  // Terminal phase: Failed
  if (currentPhase === 'failed') {
    const errorMessage = post?.ai_metadata?.error || 'Unknown error occurred'
    return (
      <>
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-2xl font-semibold text-foreground mb-2">Generation Failed</h3>
          <p className="text-muted-foreground mb-6">{errorMessage}</p>
          <Button onClick={() => closeDrawer(drawerId)}>Close</Button>
        </div>
      </>
    )
  }

  // Approval phase: Headline
  // If user took action, show loading instead of approval screen to prevent flicker
  if (currentPhase === 'awaiting_headline_approval' && post) {
    if (actionTaken === 'headline_approved' || actionTaken === 'headline_revision') {
      return (
        <BlogGenerationLoadingState
          postId={postId}
          currentPhase={actionTaken === 'headline_approved' ? 'outlining' : 'generating_headlines'}
          onCancel={() => closeDrawer(drawerId)}
        />
      )
    }

    return (
      <HeadlineReviewContent
        post={post as any}
        onApprove={() => {
          setActionTaken('headline_approved')
          toast.success('Continuing with generation...')
        }}
        onRevise={() => {
          setActionTaken('headline_revision')
          toast.info('Generating new headlines...')
        }}
      />
    )
  }

  // Approval phase: Outline
  // If user took action, show loading instead of approval screen to prevent flicker
  if (currentPhase === 'awaiting_outline_approval' && post) {
    if (actionTaken === 'outline_approved' || actionTaken === 'outline_revision') {
      return (
        <BlogGenerationLoadingState
          postId={postId}
          currentPhase={actionTaken === 'outline_approved' ? 'writing' : 'outlining'}
          onCancel={() => closeDrawer(drawerId)}
        />
      )
    }

    return (
      <OutlineReviewContent
        post={post as any}
        onApprove={() => {
          setActionTaken('outline_approved')
          toast.success('Continuing with generation...')
        }}
        onRevise={() => {
          setActionTaken('outline_revision')
          toast.info('Regenerating outline...')
        }}
      />
    )
  }

  // Approval phase: Length decision
  // If user took action, show loading instead of approval screen to prevent flicker
  if (currentPhase === 'awaiting_length_decision' && post) {
    if (actionTaken === 'length_decision') {
      return (
        <BlogGenerationLoadingState
          postId={postId}
          currentPhase="optimizing"
          onCancel={() => closeDrawer(drawerId)}
        />
      )
    }

    return (
      <LengthWarningContent
        post={post as any}
        onDecision={() => {
          setActionTaken('length_decision')
          toast.success('Continuing with generation...')
        }}
      />
    )
  }

  // Fallback: Still loading
  return (
    <BlogGenerationLoadingState
      postId={postId}
      currentPhase={currentPhase}
      onCancel={() => closeDrawer(drawerId)}
    />
  )
}
